import React, { useCallback, useEffect, useState } from 'react';
import { Send, Copy, MessageCircle, Smartphone, Loader2, CheckCircle2, FileDown, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const downloadSigned = async (req) => {
  try {
    const r = await api.get(`/signature-requests/${req.id}/signed-pdf`, { responseType: 'blob' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    link.download = `${req.doc_type}_signe.pdf`;
    link.click();
    setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
  } catch (e) { toast.error('Téléchargement impossible'); }
};

const LinkDialog = ({ open, onClose, docType, docId, onChanged }) => {
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const create = useCallback(async (signerPhone) => {
    setBusy(true);
    try {
      const r = await api.post('/signature-requests', { doc_type: docType, doc_id: docId, signer_phone: signerPhone || null });
      setData(r.data); setPhone(r.data.signer_phone || ''); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Impossible de créer le lien'); onClose(); } finally { setBusy(false); }
  }, [docType, docId, onChanged, onClose]);

  useEffect(() => { if (open) { setData(null); create(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.url); toast.success('Lien copié'); } catch (e) { toast.error('Copie impossible'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md min-w-0 overflow-x-hidden" data-testid="remote-sign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Signature à distance</DialogTitle>
          <DialogDescription>{data?.title || 'Préparation du lien…'}</DialogDescription>
        </DialogHeader>
        {!data ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div> : (
          <div className="min-w-0 space-y-3">
            <p className="text-sm text-gray-600">Signataire : <b>{data.signer_name || '—'}</b> · lien valable jusqu'au {fmt(data.expires_at)}</p>
            <div className="flex min-w-0 gap-2">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Téléphone du signataire"
                className="min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-[#B85C38] outline-none" data-testid="remote-sign-phone-input" />
              <Button variant="outline" disabled={busy} onClick={() => create(phone)} className="rounded-xl" data-testid="remote-sign-phone-save">OK</Button>
            </div>
            <div className="rounded-xl bg-[#F5EFE1] p-3 text-xs text-[#2B3A2C] break-all" data-testid="remote-sign-message">{data.message}</div>
            <div className="grid grid-cols-3 gap-2">
              <a href={data.whatsapp_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 rounded-xl bg-[#25D366] text-white py-2.5 text-xs font-bold hover:opacity-90" data-testid="remote-sign-whatsapp">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
              <a href={data.sms_url} className="flex flex-col items-center gap-1 rounded-xl bg-[#2B3A2C] text-white py-2.5 text-xs font-bold hover:opacity-90" data-testid="remote-sign-sms">
                <Smartphone className="w-5 h-5" /> SMS
              </a>
              <button type="button" onClick={copy} className="flex flex-col items-center gap-1 rounded-xl border-2 border-[#B85C38] text-[#B85C38] py-2 text-xs font-bold" data-testid="remote-sign-copy">
                <Copy className="w-5 h-5" /> Copier
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const RemoteSignButton = ({ docType, docId, size = 'sm', className = '', compact = false }) => {
  const [open, setOpen] = useState(false);
  const [last, setLast] = useState(null);
  const load = useCallback(() => {
    if (!docId) return;
    api.get('/signature-requests', { params: { doc_type: docType, doc_id: docId } })
      .then((r) => setLast(r.data[0] || null)).catch(() => {});
  }, [docType, docId]);
  useEffect(() => { load(); }, [load]);

  const testBase = `remote-sign-${docType}-${docId}`;
  if (compact) {
    const signed = last?.status === 'signed';
    return (
      <>
        <Button variant="ghost" size="icon" onClick={() => (signed ? downloadSigned(last) : setOpen(true))}
          title={signed ? `Signé par ${last.signed_name} le ${fmt(last.signed_at)} — télécharger le PDF signé` : last?.status === 'pending' ? 'Signature en attente — renvoyer le lien' : 'Envoyer pour signature (WhatsApp / SMS)'}
          className={signed ? 'text-emerald-600 hover:bg-emerald-50' : last?.status === 'pending' ? 'text-amber-600 hover:bg-amber-50' : 'text-[#128C4A] hover:bg-[#25D366]/10'}
          data-testid={`${testBase}-${signed ? 'download' : 'btn'}`}>
          {signed ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
        </Button>
        <LinkDialog open={open} onClose={() => setOpen(false)} docType={docType} docId={docId} onChanged={load} />
      </>
    );
  }
  if (last?.status === 'signed') {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`} data-testid={`${testBase}-signed`}>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-[11px] font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" /> Signé par {last.signed_name} le {fmt(last.signed_at)}
        </span>
        <Button size={size} variant="outline" onClick={() => downloadSigned(last)} className="rounded-xl" data-testid={`${testBase}-download`}>
          <FileDown className="w-4 h-4 mr-1" /> PDF signé
        </Button>
      </div>
    );
  }
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <Button size={size} variant="outline" onClick={() => setOpen(true)} className="rounded-xl border-[#25D366]/50 text-[#128C4A] hover:bg-[#25D366]/10" data-testid={`${testBase}-btn`}>
        <Send className="w-4 h-4 mr-1" /> Envoyer pour signature
      </Button>
      {last?.status === 'pending' && (
        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" data-testid={`${testBase}-pending`}>
          <Clock3 className="w-3.5 h-3.5" /> En attente
        </span>
      )}
      <LinkDialog open={open} onClose={() => setOpen(false)} docType={docType} docId={docId} onChanged={load} />
    </div>
  );
};

export default RemoteSignButton;
